// frontend/src/pages/NewUIResetPassword.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from '../config/axios';

// Botón gaming reutilizable
function GamingButton({ variant = "secondary", className = "", leftIcon, children, disabled, ...props }) {
    const base =
        "inline-flex items-center justify-center gap-2.5 " +
        "h-12 md:h-14 px-6 md:px-8 " +
        "rounded-[14px] md:rounded-[16px] " +
        "font-bold tracking-wider leading-none " +
        "transition-transform duration-150 hover:scale-[1.02] active:scale-[0.98] " +
        "disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100";

    const variants = {
        primary: "bg-accent text-secondary hover:bg-accent-light border-2 border-accent-dark",
        secondary: "bg-primary text-white hover:bg-primary-light border-2 border-primary-dark",
        ctaBlue: "bg-[#2284DF] text-white hover:bg-[#2A91F4] border-2 border-[#1E75C6]",
        ctaYellow: "bg-[#FCE503] text-[#122436] hover:bg-[#FCE503]/90 border-2 border-[#E5CF03]",
    };

    return (
        <button
            className={`${base} ${variants[variant]} ${className}`}
            disabled={disabled}
            {...props}
        >
            {leftIcon && <span className="inline-flex items-center">{leftIcon}</span>}
            <span className="whitespace-nowrap">{children}</span>
        </button>
    );
}

const NewUIResetPassword = () => {
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: '',
    });
    const [token, setToken] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');
    const [validationErrors, setValidationErrors] = useState({});
    const navigate = useNavigate();
    const location = useLocation();

    // Aplicar scope de Acumin
    useEffect(() => {
        document.body.classList.add('acumin-scope');
        return () => {
            document.body.classList.remove('acumin-scope');
        };
    }, []);

    useEffect(() => {
        // Obtener el token de la URL
        const queryParams = new URLSearchParams(location.search);
        const tokenFromUrl = queryParams.get('token');

        if (!tokenFromUrl) {
            setError('Token no proporcionado. Por favor, utilizá el enlace enviado a tu correo electrónico.');
            return;
        }

        setToken(tokenFromUrl);
    }, [location]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });

        // Limpiar errores específicos al cambiar un campo
        if (validationErrors[e.target.name]) {
            setValidationErrors({
                ...validationErrors,
                [e.target.name]: ''
            });
        }
    };

    const validateForm = () => {
        const errors = {};

        if (formData.password.length < 6) {
            errors.password = 'La contraseña debe tener al menos 6 caracteres';
        }

        if (formData.password !== formData.confirmPassword) {
            errors.confirmPassword = 'Las contraseñas no coinciden';
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setError('');
        setIsSubmitting(true);

        try {
            await axios.post('/auth/reset-password', {
                token,
                password: formData.password
            });

            setSuccess(true);

            // Redirigir al login después de 3 segundos
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al restablecer la contraseña');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center pt-24 pb-12 px-4 sm:px-6 lg:px-8"
            style={{
                backgroundImage: 'url(/images/backgrounds/hero-radial-bg.png)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <div className="w-full max-w-md">
                {/* Logo/Header */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-block">
                        <h1
                            className="toon toon-3d text-4xl md:text-5xl font-extrabold uppercase"
                            style={{
                                WebkitTextStroke: "1px #000",
                                textShadow: "0 2px 0 rgba(0,0,0,1), 0 4px 2px rgba(0,0,0,1)"
                            }}
                        >
                            Mano Ganadora
                        </h1>
                    </Link>
                </div>

                {/* Card */}
                <div
                    className="rounded-[24px] overflow-hidden shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
                    style={{
                        backgroundColor: '#122436',
                        border: '2px solid rgba(255,255,255,0.55)'
                    }}
                >
                    <div className="px-6 sm:px-8 py-8">
                        <h2 className="text-2xl md:text-3xl font-extrabold text-white text-center mb-6 uppercase tracking-wider">
                            Nueva Contraseña
                        </h2>

                        {success ? (
                            <div className="text-center space-y-6">
                                {/* Success Icon */}
                                <div className="flex justify-center mb-4">
                                    <div
                                        className="w-16 h-16 rounded-full flex items-center justify-center"
                                        style={{
                                            backgroundColor: 'rgba(34, 223, 100, 0.15)',
                                            border: '3px solid #22DF64'
                                        }}
                                    >
                                        <svg
                                            className="w-8 h-8 text-[#22DF64]"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={3}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    </div>
                                </div>

                                <div
                                    className="rounded-lg p-4 mb-6"
                                    style={{
                                        backgroundColor: 'rgba(34, 223, 100, 0.15)',
                                        border: '2px solid #22DF64'
                                    }}
                                >
                                    <p className="text-white/90 font-bold">
                                        ¡Tu contraseña ha sido actualizada con éxito!
                                    </p>
                                </div>

                                <p className="text-sm text-white/70">
                                    Serás redirigido al inicio de sesión en unos segundos...
                                </p>

                                <Link to="/login" className="block">
                                    <GamingButton
                                        variant="ctaYellow"
                                        className="w-full text-base uppercase"
                                    >
                                        Ir al Login
                                    </GamingButton>
                                </Link>
                            </div>
                        ) : (
                            <>
                                {error && (
                                    <div
                                        className="mb-6 rounded-lg p-4 text-sm"
                                        style={{
                                            backgroundColor: 'rgba(253, 2, 3, 0.15)',
                                            border: '2px solid #FD0203',
                                            color: '#FFB3B3'
                                        }}
                                    >
                                        {error}
                                    </div>
                                )}

                                {!token ? (
                                    <div className="text-center space-y-6">
                                        <p className="text-sm text-white/80 mb-6">
                                            El enlace de restablecimiento de contraseña no es válido o ha expirado.
                                            Por favor, solicitá un nuevo enlace de restablecimiento.
                                        </p>
                                        <Link to="/forgot-password" className="block">
                                            <GamingButton
                                                variant="ctaBlue"
                                                className="w-full text-base uppercase"
                                            >
                                                Solicitar Nuevo Enlace
                                            </GamingButton>
                                        </Link>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-5">
                                        {/* Password Input */}
                                        <div>
                                            <label
                                                htmlFor="password"
                                                className="block text-sm font-bold text-white/90 mb-2 uppercase tracking-wide"
                                            >
                                                Nueva Contraseña
                                            </label>
                                            <input
                                                id="password"
                                                name="password"
                                                type="password"
                                                required
                                                value={formData.password}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 rounded-lg bg-[#0E2238] border-2 
                                                         text-white placeholder-white/40
                                                         focus:border-[#2284DF] focus:outline-none focus:ring-2 focus:ring-[#2284DF]/50
                                                         transition-all
                                                         ${validationErrors.password ? 'border-[#FD0203]' : 'border-white/20'}`}
                                                placeholder="••••••••"
                                            />
                                            {validationErrors.password && (
                                                <p className="mt-2 text-xs text-[#FFB3B3] font-semibold">
                                                    {validationErrors.password}
                                                </p>
                                            )}
                                        </div>

                                        {/* Confirm Password Input */}
                                        <div>
                                            <label
                                                htmlFor="confirmPassword"
                                                className="block text-sm font-bold text-white/90 mb-2 uppercase tracking-wide"
                                            >
                                                Confirmar Contraseña
                                            </label>
                                            <input
                                                id="confirmPassword"
                                                name="confirmPassword"
                                                type="password"
                                                required
                                                value={formData.confirmPassword}
                                                onChange={handleChange}
                                                className={`w-full px-4 py-3 rounded-lg bg-[#0E2238] border-2 
                                                         text-white placeholder-white/40
                                                         focus:border-[#2284DF] focus:outline-none focus:ring-2 focus:ring-[#2284DF]/50
                                                         transition-all
                                                         ${validationErrors.confirmPassword ? 'border-[#FD0203]' : 'border-white/20'}`}
                                                placeholder="••••••••"
                                            />
                                            {validationErrors.confirmPassword && (
                                                <p className="mt-2 text-xs text-[#FFB3B3] font-semibold">
                                                    {validationErrors.confirmPassword}
                                                </p>
                                            )}
                                        </div>

                                        {/* Submit Button */}
                                        <div className="pt-2">
                                            <GamingButton
                                                type="submit"
                                                variant="ctaYellow"
                                                className="w-full text-base uppercase"
                                                disabled={isSubmitting}
                                            >
                                                {isSubmitting ? 'Procesando...' : 'Cambiar Contraseña'}
                                            </GamingButton>
                                        </div>
                                    </form>
                                )}

                                <div className="mt-6 text-center">
                                    <Link
                                        to="/login"
                                        className="text-sm text-[#2284DF] hover:text-[#2A91F4] font-semibold transition-colors"
                                    >
                                        Volver al Inicio de Sesión
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Back to home link */}
                <div className="mt-6 text-center">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors font-semibold"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M10 19l-7-7m0 0l7-7m-7 7h18"
                            />
                        </svg>
                        Volver al inicio
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default NewUIResetPassword;